import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrandingContext } from '../contexts/BrandingContext';
import { Icon } from '../components/Icon';
import '../styles/LoginPage.css';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, signup, loginWithGoogle, error, user, isApproved, loading: authLoading } = useAuth();
  const { settings } = useBrandingContext();

  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showUserState, setShowUserState] = useState(false);

  // If already authenticated and approved, redirect to dashboard
  useEffect(() => {
    if (user && isApproved && !authLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, isApproved, authLoading, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setLoading(true);
    setShowUserState(false);

    try {
      if (isSignup) {
        // Validate signup form
        if (!formData.name.trim()) {
          setFormError('Name is required');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setFormError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setFormError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        console.log('Starting signup...');
        await signup(formData.email, formData.password, formData.name, 'viewer');
        console.log('Signup successful, waiting for auth state...');
        // Wait for auth state to update
        await new Promise(resolve => setTimeout(resolve, 1200));
        setShowUserState(true);
        setLoading(false);
      } else {
        console.log('Starting login with email:', formData.email);
        await login(formData.email, formData.password);
        console.log('Login successful, waiting for auth state...');
        // Wait for auth state to update
        await new Promise(resolve => setTimeout(resolve, 1200));
        setShowUserState(true);
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setFormError(err.message || (isSignup ? 'Signup failed' : 'Login failed'));
      setLoading(false);
      setShowUserState(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setFormError('');
      setLoading(true);
      setShowUserState(false);
      await loginWithGoogle();
      console.log('Google sign-in successful, waiting for auth state...');
      // Wait for Firestore data to load
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowUserState(true);
      setLoading(false);
    } catch (err: any) {
      setFormError(err.message || 'Google sign-in failed');
      setLoading(false);
      setShowUserState(false);
    }
  };

  const toggleMode = () => {
    setIsSignup(!isSignup);
    setFormError('');
    setFormData({
      email: '',
      password: '',
      name: '',
      confirmPassword: '',
    });
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          {settings?.branding.logoUrl && (
            <img
              src={settings.branding.logoUrl}
              alt={settings.branding.logoAlt}
              style={{ height: '50px', marginBottom: '12px', maxWidth: '100%', objectFit: 'contain' }}
            />
          )}
          <h1>{settings?.branding.loginPageTitle || 'Redmax Quoting'}</h1>
          <p>{isSignup ? (settings?.branding.loginSignupText || 'Create Account') : (settings?.branding.loginPageSubtitle || 'Sign In')}</p>
        </div>

        {(formError || error) && (
          <div className="error-message">
            {formError || error}
          </div>
        )}

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{isSignup ? 'Creating account...' : 'Signing in...'}</p>
          </div>
        )}

        {showUserState && user && !isApproved && !loading && (
          <div className="status-message pending-status">
            <div className="status-icon">⏳</div>
            <h3>Account Pending Approval</h3>
            <p>Your account has been created and is awaiting admin approval. You'll receive an email confirmation once your account has been approved.</p>
            <button
              type="button"
              onClick={toggleMode}
              className="toggle-button-status"
            >
              Try another account
            </button>
          </div>
        )}

        {!loading && !showUserState && (
          <>
            <form onSubmit={handleSubmit} className="login-form">
              {isSignup && (
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 6 characters"
                  required
                />
              </div>

              {isSignup && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    required
                  />
                </div>
              )}

              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? (isSignup ? 'Creating Account...' : 'Signing In...') : (isSignup ? 'Create Account' : 'Sign In')}
              </button>
            </form>

            <div className="divider">
              <span>or</span>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="google-button"
              disabled={loading}
            >
              <span className="google-icon">
                <Icon name="search" size={18} />
              </span>
              Sign in with Google
            </button>

            <div className="login-footer">
              <p>
                {isSignup ? 'Already have an account?' : "Don't have an account?"}
                <button
                  type="button"
                  onClick={toggleMode}
                  className="toggle-button"
                  disabled={loading}
                >
                  {isSignup ? 'Sign In' : 'Sign Up'}
                </button>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
